import { useMemo, useState } from "react";
import { comics, styles, type BlComic } from "@/mocks/blcomics";

interface LibraryProps {
  query: string;
  onQueryChange: (v: string) => void;
}

const sortOptions = ["推荐", "评分", "更新"] as const;
type SortKey = (typeof sortOptions)[number];

function ComicCard({ comic }: { comic: BlComic }) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition hover:-translate-y-1">
      <div className="relative aspect-[3/4] w-full overflow-hidden bg-background-200">
        <img
          src={comic.cover}
          alt={`BL漫画《${comic.title}》${comic.style}画风封面`}
          title={`${comic.title} ${comic.style}画风`}
          className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
        />
        <span className="absolute left-2 top-2 whitespace-nowrap rounded-full bg-primary-500/90 px-2.5 py-1 text-xs font-medium text-background-50">
          {comic.style}
        </span>
        <span className="absolute right-2 top-2 whitespace-nowrap rounded-full bg-black/45 px-2 py-1 text-xs font-semibold text-amber-200 backdrop-blur">
          <i className="ri-star-fill mr-0.5 text-amber-300" aria-hidden="true"></i>
          {comic.rating.toFixed(1)}
        </span>
        <span
          className={`absolute bottom-2 left-2 whitespace-nowrap rounded-full px-2.5 py-1 text-xs ${
            comic.status === "连载" ? "bg-accent-500/90 text-background-50" : "bg-background-50/90 text-foreground-700"
          }`}
        >
          {comic.status}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-3 md:p-4">
        <h3 className="text-sm font-semibold text-foreground-900 md:text-base">{comic.title}</h3>
        <p className="mt-1 text-xs text-foreground-500">
          {comic.author} · {comic.type} · {comic.chapters}话
        </p>
        <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600 md:text-sm">{comic.desc}</p>
        <div className="mt-auto flex flex-wrap gap-1.5 pt-3">
          {comic.tags.slice(0, 3).map((t) => (
            <span key={t} className="whitespace-nowrap rounded-full bg-background-100 px-2 py-0.5 text-xs text-foreground-600">
              {t}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}

export default function Library({ query, onQueryChange }: LibraryProps) {
  const [activeStyle, setActiveStyle] = useState<string>("全部");
  const [sort, setSort] = useState<SortKey>("推荐");

  const filtered = useMemo(() => {
    let list = [...comics];
    if (activeStyle !== "全部") list = list.filter((c) => c.style === activeStyle);
    if (query.trim()) {
      const q = query.trim();
      list = list.filter(
        (c) =>
          c.title.includes(q) || c.author.includes(q) || c.type.includes(q) || c.style.includes(q) || c.tags.some((t) => t.includes(q))
      );
    }
    if (sort === "评分") list.sort((a, b) => b.rating - a.rating);
    if (sort === "更新") list.sort((a, b) => Number(b.year) - Number(a.year));
    return list;
  }, [activeStyle, query, sort]);

  return (
    <section id="library" className="scroll-mt-20 bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p className="text-xs font-medium tracking-widest text-primary-600">BL漫画画风筛选列表</p>
            <h2 className="mt-2 font-heading text-2xl text-foreground-900 md:text-4xl">画风库 · 精美细腻作品</h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
              点击下方画风标签即可筛选，也支持关键词搜索与排序，快速找到作画好看、画风细腻的作品。
            </p>
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-4 border-y border-background-200 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setActiveStyle("全部")}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition ${
                activeStyle === "全部"
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              全部
            </button>
            {styles.map((s) => (
              <button
                key={s.name}
                type="button"
                onClick={() => setActiveStyle(s.name)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition ${
                  activeStyle === s.name
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {s.name}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400" aria-hidden="true"></i>
              <input
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                type="text"
                placeholder="搜索作品 / 作者 / 题材"
                className="h-10 w-full rounded-full border border-background-300 bg-background-50 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none lg:w-52"
              />
            </div>
            <div className="flex items-center rounded-full bg-background-100 p-1">
              {sortOptions.map((o) => (
                <button
                  key={o}
                  type="button"
                  onClick={() => setSort(o)}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition ${
                    sort === o ? "bg-background-50 text-primary-600 shadow-soft" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {o}
                </button>
              ))}
            </div>
          </div>
        </div>

        <p className="mt-5 text-sm text-foreground-500">
          共筛选出 <span className="font-semibold text-primary-600">{filtered.length}</span> 部
          {activeStyle !== "全部" ? `「${activeStyle}」画风` : ""}作品
        </p>

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-5">
            {filtered.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/60 py-16 text-center">
            <i className="ri-search-eye-line text-4xl text-foreground-300" aria-hidden="true"></i>
            <p className="mt-4 text-sm text-foreground-600">没有找到匹配的作品</p>
            <button
              type="button"
              onClick={() => {
                onQueryChange("");
                setActiveStyle("全部");
              }}
              className="mt-4 whitespace-nowrap rounded-full bg-secondary-500 px-5 py-2 text-sm font-medium text-background-50 transition hover:bg-secondary-600"
            >
              清空筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}